import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import {
  getAllMetricFieldAttributes,
  type MetricFieldAttributes,
} from '@/utils/attributes/metricField';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Autocomplete, DialogContent, Skeleton, TextField, Typography } from '@mui/material';
import { type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import SET_DEVICE_MAIN_METRIC from '../../graphql/mutations/setDeviceMainMetric';
import GET_DEVICE_MAIN_METRIC from '../../graphql/queries/getDeviceMainMetric';

interface Props {
  deviceId: string;
  onClose: () => void;
}

interface FormValues {
  metricField: MetricFieldAttributes | null;
}

const Component: FC<Props> = ({ deviceId, onClose }) => {
  const { _, i18n } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const options = getAllMetricFieldAttributes(i18n);
  const { data } = useSuspenseQuery(GET_DEVICE_MAIN_METRIC, {
    variables: { deviceId },
  });

  const [setMainMetric] = useMutation(SET_DEVICE_MAIN_METRIC, {
    onCompleted() {
      openModalSuccessMessage(_(msg`La métrica principal se ha actualizado correctamente`));
      onClose();
    },
    onError(error) {
      openModalErrorMessage(_(msg`Error al actualizar la métrica principal: ${error.message}`));
    },
    update(cache) {
      cache.modify({
        id: cache.identify({ __typename: 'Device', id: deviceId }),
        fields: {
          mainMetric(_, { INVALIDATE }) {
            return INVALIDATE;
          },
        },
      });
    },
    onQueryUpdated(observableQuery) {
      return observableQuery.refetch();
    },
  });

  const currentMetricField = data.device.mainMetric?.metricField;

  const methods = useForm<FormValues>({
    defaultValues: {
      metricField: options.find((option) => option.value === currentMetricField) ?? null,
    },
  });

  const submitHandler = async (values: FormValues) => {
    if (!values.metricField) return;
    await setMainMetric({
      variables: { input: { deviceId, metricField: values.metricField.value } },
    });
  };

  return (
    <form onSubmit={methods.handleSubmit(submitHandler)}>
      <DialogContent dividers>
        <Typography mb={2}>
          <Trans>
            Configurar la métrica principal de <b>{data.device.profile.name}</b>
          </Trans>
        </Typography>
        <Controller
          control={methods.control}
          name="metricField"
          render={({ field }) => (
            <Autocomplete
              options={options}
              getOptionLabel={(option) => option.dataLabel}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              renderInput={(params) => (
                <TextField {...params} label={_(msg`Métrica principal`)} />
              )}
            />
          )}
        />
      </DialogContent>
      <ModalActions
        onClose={onClose}
        dirtyForm={methods.formState.isDirty}
        onSubmit="submit"
        onResetForm={() => methods.reset()}
        submitLoading={methods.formState.isSubmitting}
      />
    </form>
  );
};

const Fallback: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { _ } = useLingui();
  return (
    <DialogContent>
      <ModalTitle title={_(msg`Métrica principal del dispositivo`)} onClose={onClose} />
      <Skeleton height={150} variant="rounded" />
    </DialogContent>
  );
};

const DeviceMainMetricModal: FC = () => {
  const { _ } = useLingui();
  const { globalModalProps, state, onClose } = useGlobalModal('updateDeviceMainMetricModal');

  return (
    <GlobalModal {...globalModalProps} suspenseFallback={<Fallback onClose={onClose} />}>
      {state.open && (
        <>
          <ModalTitle title={_(msg`Métrica principal del dispositivo`)} onClose={onClose} />
          <Component deviceId={state.deviceId} onClose={onClose} />
        </>
      )}
    </GlobalModal>
  );
};

export default DeviceMainMetricModal;
